import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { AdminClient } from "../../../api/generated/APIClient";
import { getImageUrl } from "../../../utils/imageFetcher";
import { ResultCategoryDetailPage } from "./ResultCategoryDetailPage";
import { ResultCategoryFormPage } from "./ResultCategoryFormPage";
import "./ResultCategoryPage.css";

// ルートモードの型
type RouteMode = "list" | "detail" | "edit" | "create";

export const ResultCategoryPage: React.FC = () => {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [categories, setCategories] = useState<any[]>([]);
  const [results, setResults] = useState<any[]>([]);
  const [currentCategory, setCurrentCategory] = useState<any | undefined>();

  // category.id -> 表示用サムネ URL（thumbnail_url が空ならカテゴリ所属 Result の main_image を流用）
  const categoryThumbMap = useMemo(() => {
    const map = new Map<string, string>();
    for (const cat of categories) {
      const explicit = cat.thumbnailUrl || cat.thumbnail_url;
      if (explicit) {
        const url = getImageUrl(explicit);
        if (url) map.set(cat.id, url);
        continue;
      }
      const hit = results.find((r) => (r.categoryId || r.category_id) === cat.id);
      const sp = hit?.mainImage?.storagePath || hit?.main_image?.storage_path;
      if (sp) {
        const url = getImageUrl(sp);
        if (url) map.set(cat.id, url);
      }
    }
    return map;
  }, [categories, results]);

  // ルートからモードを判定
  const getMode = (): RouteMode => {
    const path = location.pathname;
    if (path.includes("/edit")) return "edit";
    if (path.includes("/create")) return "create";
    if (id) return "detail";
    return "list";
  };

  const mode = getMode();

  // Connect-ES APIクライアント呼び出し関数
  const callSystemEntityAPI = async (
    operation: "index" | "create" | "update" | "delete",
    data?: any,
    ids?: string[]
  ) => {
    try {
      const entityPascalCase = "ResultCategory";
      let methodName: string;
      switch (operation) {
        case "index":
          methodName = `Index${entityPascalCase}`;
          break;
        case "create":
          methodName = `Create${entityPascalCase}`;
          break;
        case "update":
          methodName = `Update${entityPascalCase}`;
          break;
        case "delete":
          methodName = `Delete${entityPascalCase}`;
          break;
        default:
          throw new Error(`未対応の操作: ${operation}`);
      }

      const apiMethod = (AdminClient as any)[methodName];
      if (!apiMethod || typeof apiMethod !== "function") {
        throw new Error(`APIメソッド ${methodName} が見つかりません`);
      }

      let apiArgs: any = {};
      if (operation === "index") {
        apiArgs = {
          offset: 0,
          limit: 0,
        };
      } else if (operation === "delete" && ids) {
        apiArgs = {
          ids: ids,
        };
      } else if (data) {
        apiArgs = {
          resultCategorys: [data],
        };
      }

      const result = await apiMethod(apiArgs);
      return result;
    } catch (error) {
      console.error(`${operation} request failed:`, error);
      throw error;
    }
  };

  // データを取得
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await callSystemEntityAPI("index");
        let items: any[] = [];
        if (response && response.data) {
          if (Array.isArray(response.data)) {
            items = response.data;
          } else if (typeof response.data === "object") {
            const dataObj = response.data as any;
            if (
              dataObj.resultCategorys &&
              Array.isArray(dataObj.resultCategorys)
            ) {
              items = dataObj.resultCategorys;
            } else if (
              dataObj.result_categorys &&
              Array.isArray(dataObj.result_categorys)
            ) {
              items = dataObj.result_categorys;
            } else if (dataObj.items && Array.isArray(dataObj.items)) {
              items = dataObj.items;
            } else if (dataObj.resultCategorys === null || dataObj.items === null) {
              items = [];
            } else if (Object.keys(dataObj).length === 0) {
              items = [];
            } else if (dataObj.id) {
              items = [dataObj];
            } else {
              items = [];
            }
          }
        }
        // 表示順序でソート
        items.sort((a, b) => {
          const orderA = a.displayOrder ?? a.display_order ?? 0;
          const orderB = b.displayOrder ?? b.display_order ?? 0;
          return orderA - orderB;
        });
        setCategories(items);
      } catch (error) {
        console.error("カテゴリ取得エラー:", error);
        setCategories([]);
      }
    };

    fetchCategories();
  }, []);

  // Result 一覧も取得（カテゴリのサムネ自動補完用）
  useEffect(() => {
    const fetchResults = async () => {
      try {
        const res = await (AdminClient as any).IndexResult({ offset: 0, limit: 0 });
        const data = res?.data ?? res ?? {};
        const items: any[] = data.results ?? data.Results ?? [];
        setResults(Array.isArray(items) ? items : []);
      } catch (e) {
        console.warn("Result の取得に失敗（カテゴリサムネ補完なし）", e);
      }
    };
    void fetchResults();
  }, []);

  useEffect(() => {
    if (id) {
      const item = categories.find((item) => item.id === id);
      setCurrentCategory(item);
    } else {
      setCurrentCategory(undefined);
    }
  }, [id, categories]);

  const handleEdit = (itemId: string) => {
    navigate(`/admin/entities/result-category/${itemId}/edit`);
  };

  const handleDelete = async (itemId: string) => {
    if (!confirm("カテゴリを削除しますか？\n※このカテゴリに属する施工実績は「未分類」になります。")) {
      return;
    }

    try {
      await callSystemEntityAPI("delete", undefined, [itemId]);
      setCategories(categories.filter((item) => item.id !== itemId));
      if (id === itemId) {
        navigate(`/admin/entities/result-category`);
      }
    } catch (error) {
      console.error("削除エラー:", error);
      alert(
        `削除に失敗しました: ${
          error instanceof Error ? error.message : "不明なエラー"
        }`
      );
    }
  };

  const handleCreate = () => {
    navigate(`/admin/entities/result-category/create`);
  };

  const handleBack = () => {
    navigate(`/admin/entities/result-category`);
  };

  const handleDetail = (itemId: string) => {
    navigate(`/admin/entities/result-category/${itemId}`);
  };

  // 作成・編集モードの場合は専用フォームページを表示
  if (mode === "create" || mode === "edit") {
    return <ResultCategoryFormPage />;
  }

  // 一覧モードの場合
  if (mode === "list") {
    return (
      <div className="result-category-list-page">
        <div className="result-category-list-header">
          <h1>施工実績カテゴリ一覧</h1>
          <div className="result-category-list-actions">
            <button className="btn btn-primary" onClick={handleCreate}>
              新規作成
            </button>
          </div>
        </div>

        <div className="result-category-list-content">
          {categories.length === 0 ? (
            <div className="result-category-empty-state">
              <p>カテゴリがありません</p>
              <button className="btn btn-primary" onClick={handleCreate}>
                新規作成
              </button>
            </div>
          ) : (
            <div className="result-category-list-table-container">
              <table className="result-category-list-table">
                <thead>
                  <tr>
                    <th className="col-order">順序</th>
                    <th className="col-thumbnail">画像</th>
                    <th className="col-name">カテゴリ名</th>
                    <th className="col-description">説明</th>
                    <th className="col-status">状態</th>
                    <th className="col-actions">操作</th>
                  </tr>
                </thead>
                <tbody>
                  {categories.map((category) => (
                    <tr key={category.id}>
                      <td className="col-order">
                        {category.displayOrder ?? category.display_order ?? 0}
                      </td>
                      <td className="col-thumbnail">
                        {categoryThumbMap.get(category.id) ? (
                          <img
                            src={categoryThumbMap.get(category.id)}
                            alt={category.name}
                            className="category-thumbnail"
                            onClick={() => handleDetail(category.id)}
                          />
                        ) : (
                          <div
                            className="category-thumbnail-placeholder"
                            onClick={() => handleDetail(category.id)}
                          >
                            No Image
                          </div>
                        )}
                      </td>
                      <td className="col-name">
                        <Link
                          to={`/admin/entities/result-category/${category.id}`}
                          className="category-name-link"
                        >
                          {category.name}
                        </Link>
                      </td>
                      <td className="col-description">
                        {category.description || "-"}
                      </td>
                      <td className="col-status">
                        <span
                          className={`status-badge ${
                            category.isEnabled ?? category.is_enabled
                              ? "enabled"
                              : "disabled"
                          }`}
                        >
                          {category.isEnabled ?? category.is_enabled
                            ? "有効"
                            : "無効"}
                        </span>
                      </td>
                      <td className="col-actions">
                        <div className="action-buttons">
                          <button
                            className="btn btn-sm btn-primary"
                            onClick={() => handleDetail(category.id)}
                          >
                            詳細
                          </button>
                          <button
                            className="btn btn-sm btn-secondary"
                            onClick={() => handleEdit(category.id)}
                          >
                            編集
                          </button>
                          <button
                            className="btn btn-sm btn-danger"
                            onClick={() => handleDelete(category.id)}
                          >
                            削除
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    );
  }

  // 詳細モードの場合: thumbnail_url が空でも、Result から拾ったサムネを差し込んで渡す
  const detailCategory = currentCategory
    ? {
        ...currentCategory,
        thumbnail_url:
          currentCategory.thumbnailUrl ||
          currentCategory.thumbnail_url ||
          categoryThumbMap.get(currentCategory.id) ||
          "",
      }
    : currentCategory;

  return (
    <ResultCategoryDetailPage
      category={detailCategory}
      onEdit={handleEdit}
      onDelete={handleDelete}
      onBack={handleBack}
    />
  );
};
